import React, { useState, useEffect } from "react";
import moment from "moment";
import styled from "styled-components";
import useFetch from '../../../hooks/useFetch'
import { Modal, Button, LoadingIcon, SelectDropdown } from '@prosolve/common-components-ui'
import Table from "../../sharedComponents/Table";
import useIsMobileView from "../../../hooks/useIsMobileView";
import { FaCheck } from 'react-icons/fa';
import useAccounts from "../../../dataHooks/useDataAccounts";
import DisplayAssociatedEvents from "./DisplayAssociatedEvents";

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 10px;
  border: solid 1px silver;
  border-radius: 5px;
  box-shadow: 1px 2px 5px rgb(0 0 0 / 10%);
`

const SectionTitle = styled.div`
  font-size: 18px;
  font-weight: bold;
`

export default ({
  eventData,
  onClose,
}) => {
  const {
    accountId,
    accountName
  } = eventData

  const isMobileView = useIsMobileView();

  const [isLoading, setIsLoading] = useState(false);

  const [subscriptions, setSubscriptions] = useState([])
  const [associatedEvents, setAssociatedEvents] = useState([])
  const [schoolYearFilter, setSchoolYearFilter] = useState()

  const fetch = useFetch()

  const { fetchAssociatedEvents } = useAccounts()

  /** School year is August (08) to July (07) of the following year */
  const getSchoolYearForDate = (date) => {
    const dateMt = moment(date)
    return dateMt.year() + (dateMt.isBefore(moment(`${dateMt.year()}-07-31`)) ? -1 : 0)
  }

  const getAccountSubscriptions = async () => {
    setIsLoading(true)
    try {
      const response = await fetch(`accounts/account/subscriptions?accountId=${accountId}`, { method: 'GET' })
      const subs = await response.json()
      setSubscriptions(subs)
    } catch (e) {
      setSubscriptions([])
    }
    setIsLoading(false)
  }

  const getAssociatedEvents = async () => {
    try {
      const events = await fetchAssociatedEvents(accountId)
      setAssociatedEvents(events)
    } catch (e) {
      setAssociatedEvents([])
    }
  }

  const init = async () => {
    await Promise.all([
      getAccountSubscriptions(),
      getAssociatedEvents()
    ])
  }

  useEffect(() => {
    init()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const associatedEventsUpcoming = associatedEvents.filter(e => moment(e.endDate).isAfter(moment()))
  const associatedEventsPast = associatedEvents
    .filter(e => !schoolYearFilter ? true : [getSchoolYearForDate(e.startDate), getSchoolYearForDate(e.endDate)].includes(schoolYearFilter))
    .filter(e => moment(e.endDate).isBefore(moment()))
    .sort((a,b) => moment(b.endDate) - moment(a.endDate))

  const schoolYearFilterOptions = new Set()
  associatedEventsPast.forEach(e => {
    schoolYearFilterOptions.add(getSchoolYearForDate(e.startDate))
    schoolYearFilterOptions.add(getSchoolYearForDate(e.endDate))
  })
  const schoolYearFilterOptionsArr = [...schoolYearFilterOptions].sort((a,b) => b - a)

  const footer = (
    <div style={{ 
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      columnGap: '10px',
      padding: '10px'
    }}>
      <Button onClick={onClose}>
        {'Close'}
      </Button>
    </div>
  )

  const columns = [
    {
      field: 'name',
      title: 'Subscription',
    },
    // {
    //   field: 'comboName',
    //   title: 'Combo',
    // },
    {
      field: 'unlimitedLicenses',
      title: 'Unlimited Licenses',
      renderer: (data = {}) => {
        const { unlimitedLicenses } = data;
        return unlimitedLicenses ? (
          <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
            <FaCheck style={{ marginLeft: '10px' }} />
          </div>
        ) : <></>
      }
    },
    // {
    //   field: 'subscriptionType',
    //   title: 'Type',
    // },
    {
      field: 'notes',
      title: 'Notes',
    }
  ]

  return (
    <Modal title={'Account Information'} isAutoHeight={true} onClose={onClose} footer={footer}>
      {
        isLoading ?
        <div style={{
          display: 'flex',
          justifyContent: 'center'
        }}>
          <LoadingIcon />
        </div>
        :
        <Content>
          <Section>
            <SectionTitle>
              Account
            </SectionTitle>
            {
              subscriptions.length > 0 
              ?
              <Table
                columns={columns}
                data={subscriptions}
                mobileMode={isMobileView}
              />
              :
              <div>
                {'No subscriptions for this account.'}
              </div>
            }
          </Section>

          <Section>
            <SectionTitle>
              {`Upcoming Events for ${accountName}`}
            </SectionTitle>
            <DisplayAssociatedEvents events={associatedEventsUpcoming} />
          </Section>

          <Section>
            <SectionTitle>
              {`Past Events for ${accountName}`}
            </SectionTitle>
            <div style={{
              alignItems: 'center',
              display: 'flex',
              fontSize: '18px',
              fontWeight: 'bold',
              gap: '10px'
            }}>
              <div>School Year</div>
              <SelectDropdown 
                width='150px'
                options={schoolYearFilterOptionsArr.map(y => ({
                  value: y,
                  text: `${y}-${parseInt(y)+1}`
                }))}
                onChange={(val) => setSchoolYearFilter(val)}
              />
            </div>
            <DisplayAssociatedEvents events={associatedEventsPast} />
          </Section>

          <div style={{ paddingBottom: '30px '}}></div>
        </Content>
      }
    </Modal>
  )
}